// Seven mood collections on Browse (R-60, Alvin 10/02): get moving outside, indoor and chill, make or do something,
// browse and shop, museums and culture, see a show, meet people. One rule set, shared by the browse function (the
// `collection` filter and the `collections` counts) and the app (lib/collections.ts re-exports this file).
// Built only from what every listing in the storage list already carries: category, tags, kind, title, one_line,
// venue_name and the vibe dims (posture +1 = on your feet / -1 = seated; intensity = physical effort; energy; sociality).
// No new columns, no model calls. Counts and top-20 sanity reads: build/specs/R-60-mood-collections.md.
// Import-free on purpose (the app's Vite build reads it too).

export type CollectionItem = {
  kind?: string | null
  category: string
  tags?: string[] | null
  title: string
  one_line?: string | null
  venue_name?: string | null
  source_name?: string | null
  judged_by?: string | null
  // deno-lint-ignore no-explicit-any
  vibe?: any
}
export type Collection = { key: string; sym: string; test: (it: CollectionItem) => boolean }

const has = (it: CollectionItem, ...tags: string[]) => (it.tags ?? []).some((t) => tags.includes(t))
const dim = (it: CollectionItem, k: string): number | null => {
  const v = it.vibe?.dims?.[k]
  return typeof v === 'number' ? v : null
}
const text = (it: CollectionItem) => `${it.title} ${it.one_line ?? ''} ${it.venue_name ?? ''}`
// tech week and other career rows: their own interest (career), never a mood unless the words say so
const careerRow = (it: CollectionItem) => it.judged_by === 'techweek-2026' || has(it, 'career', 'networking', 'startup', 'b2b', 'saas', 'gtm')
// watching sport from a seat is a show, not moving
const SPECTATOR = /\b(?:vs\.?|versus|game day|tournament|championship|match(?:up)?|stadium|arena|racing|derby|rodeo)\b/i

const STAGES = ['music', 'theater', 'comedy', 'film']

// 1. Get moving outside: outdoors and you move (a hike, a ride, a paddle, a run, a climb), not a stadium seat
const SPORT_WORDS = /\b(?:hik(?:e|es|ing)|trails?|walk(?:s|ing)?|run(?:s|ning)?|bike|biking|cycl\w*|ride|paddl\w*|kayak\w*|surf\w*|swim\w*|climb\w*|yoga|stairs?|loop|summit|peak|canyon|falls)\b/i
const moving = (it: CollectionItem) => {
  if (careerRow(it) && !SPORT_WORDS.test(it.title)) return false
  if (it.category === 'day-trip') return false // a drive-there day, not a get-out-and-move pick on its own
  if (STAGES.includes(it.category) || it.category === 'nightlife' || it.category === 'food' || it.category === 'drink') return false // an outdoor concert is a show
  const outside = it.category === 'outdoors' || has(it, 'outdoor', 'hike', 'beach', 'nature')
  if (!outside) return false
  if (has(it, 'sports') && SPECTATOR.test(text(it))) return false
  const posture = dim(it, 'posture'), intensity = dim(it, 'intensity')
  if (posture !== null && posture < 0.3) return false // seated outside (an outdoor screening, a concert on the lawn)
  return has(it, 'hike', 'active') || it.category === 'fitness' || SPORT_WORDS.test(it.title) || (intensity ?? -1) >= 0.4
}

const NOT_CHILL = /\b(?:yoga|pilates|workout|fitness|gym|boxing|climbing|church|worship|services?|sermon|mass|prayer|spiritual|congregation|synagogue|mosque|soup|tacos?|restaurant|counter|bar|brewery|dinner|lunch|brunch|tasting|karaoke|party|dj|rave)\b/i

// 2. Indoor and chill: inside, unhurried, low effort (a bookstore, an arcade afternoon, a quiet gallery, a tea room)
const chill = (it: CollectionItem) => {
  // a seat at a show is See a show; a cinema (a film place, no date) is both: a quiet movie is the chill afternoon
  if (careerRow(it) || (show(it) && !(it.category === 'film' && it.kind !== 'event'))) return false
  if (it.judged_by === 'ticketmaster') return false // ticketed tours, lounges and award nights are not a slow afternoon
  if (it.kind === 'event' && has(it, 'family')) return false // storytime and kids' events
  if (it.category === 'class' || it.category === 'community' || (it.kind === 'event' && has(it, 'social'))) return false // a class is Make, a group night is Meet people
  if (has(it, 'outdoor', 'hike', 'beach') || it.category === 'outdoors' || it.category === 'day-trip') return false
  const inside = has(it, 'indoor') || it.category === 'film' || it.category === 'art'
  if (!inside) return false
  if (has(it, 'lively', 'late-night') || it.category === 'nightlife' || it.category === 'fitness') return false
  // blind round 1 (priya, 10/10): no yoga or workouts, food counters, religious services or workshops (those are Make)
  if (['food', 'drink', 'market', 'talks', 'sports'].includes(it.category)) return false
  if (has(it, 'hands-on', 'active', 'sports', 'foodie', 'drinks', 'wellness', 'networking')) return false
  if (NOT_CHILL.test(`${it.title} ${it.one_line ?? ''}`) || MAKE_WORDS.test(it.title)) return false
  const energy = dim(it, 'energy'), intensity = dim(it, 'intensity')
  if (energy !== null && energy > 0.2) return false
  if (intensity !== null && intensity > 0.3) return false
  return has(it, 'chill', 'intimate', 'bookish', 'solo-friendly', 'coffee') || (energy !== null && energy <= -0.2)
}

// 3. Make or do something: hands on, you leave having made or learned to do a thing
const MAKE_WORDS = /\b(?:workshop|class(?:es)?|lessons?|make|making|maker\w*|craft\w*|pottery|ceramic\w*|paint(?:ing)?|draw(?:ing)?|sketch\w*|print\w*|sew\w*|knit\w*|weav\w*|cook(?:ing)?|bak(?:e|ing)|candle|terrarium|woodwork\w*|jewel\w*|build|diy|hands-on|learn to|escape room|axe|archery|glass\w*|calligraph\w*)\b/i
const make = (it: CollectionItem) => {
  if (careerRow(it)) return false // tech week workshops live under Career and startups
  if (it.category === 'class') return true
  if (STAGES.includes(it.category)) return false // a show tagged hands-on is still a show
  if (['food', 'drink', 'nightlife'].includes(it.category)) return MAKE_WORDS.test(it.title) // a cooking class, not a plate of skewers
  if (has(it, 'hands-on')) return true
  return has(it, 'creative', 'learn-something') && MAKE_WORDS.test(it.title)
}

// 4. Browse and shop: markets, flea and swap meets, vintage, bookshops, record stores, makers' fairs
const SHOP_WORDS = /\b(?:market(?:place)?s?|flea|swap ?meets?|vintage|thrift\w*|antique (?:market|mall|fair|show)s?|book ?stores?|bookshops?|book fair|book sale|record (?:store|shop|fair)|shopping|boutiques?|bazaars?|makers'? (?:fair|market)|craft (?:fair|market)|pop-?up shop|emporium|mercado)\b/i
const shop = (it: CollectionItem) => {
  if (careerRow(it)) return false
  if (it.category === 'market') return true
  // restaurants, bars and stages are not shopping, even with "market" or "vintage" in the name
  if (['music', 'comedy', 'theater', 'fitness', 'food', 'drink', 'nightlife', 'film'].includes(it.category)) return false
  return SHOP_WORDS.test(it.title) || (SHOP_WORDS.test(it.one_line ?? '') && /\b(?:browse|shop|vendors?|stalls?|sellers?|stores?)\b/i.test(it.one_line ?? ''))
}

// 5. Museums and culture: museums, galleries, exhibits, historic places, tours of them, art walks
const CULTURE_WORDS = /\b(?:museums?|galler(?:y|ies)|exhibit\w*|exhibition|art ?walk|historic\w*|heritage|landmark|archives?|librar(?:y|ies)|cathedral|monument|memorial|installation|sculptures?|planetarium|observatory|botanical|historical society|state historic park|adobe)\b/i
const culture = (it: CollectionItem) => {
  if (careerRow(it)) return false
  if (it.category === 'art') return true
  if (it.judged_by === 'ticketmaster' || has(it, 'sports')) return false // games and touring shows, even at a "Memorial" venue
  if (has(it, 'family') && (it.kind === 'event' || has(it, 'seasonal'))) return false // storytime, zoo nights, kids' crafts
  if (['music', 'comedy', 'nightlife', 'fitness', 'food', 'drink', 'market'].includes(it.category)) return false
  if (CULTURE_WORDS.test(it.title)) return true
  if (it.category !== 'class' && it.category !== 'community' && CULTURE_WORDS.test(it.venue_name ?? '')) return true
  // a historic place or cultural institute with no museum word (tag-only): places, not dated events or kids' crafts
  return ['other', 'community', 'talks', 'outdoors'].includes(it.category) && it.kind !== 'event' && has(it, 'history', 'cultural', 'art-lovers') &&
    !has(it, 'hike', 'active', 'family', 'seasonal')
}

// 6. See a show: a performance you watch (music, theater, comedy, film, dance on a stage, a live game)
const SHOW_WORDS = /\b(?:live|concert|show|performance|screening|premiere|recital|symphony|orchestra|opera|ballet|musical|stand-?up|improv|festival|plays?|theat(?:er|re)|cinema|movies?|films?)\b/i
const show = (it: CollectionItem) => {
  if (careerRow(it)) return false
  if (it.category === 'class' || it.category === 'fitness') return false
  if (['music', 'theater', 'comedy', 'film'].includes(it.category)) {
    if (it.kind === 'event') return true
    return SHOW_WORDS.test(text(it)) || has(it, 'live-music') // a place: a venue with shows, not a record shop or a film museum
  }
  if (it.kind === 'event' && (has(it, 'sports') || it.judged_by === 'ticketmaster') && SPECTATOR.test(text(it))) return true // a game you watch
  if (it.kind === 'event' && has(it, 'live-music')) return true
  return it.kind === 'event' && SHOW_WORDS.test(it.title) && (dim(it, 'posture') ?? 0) < 0
}

// 7. Meet people: things you do alongside others (classes, meetups, group rides, social runs, open mics, markets, clubs,
//    volunteering, game nights). Not a seat in a crowd: concerts, games and screenings are shows, not meeting people.
const MEET_WORDS = /\b(?:meet-?ups?|meet & greet|mixers?|socials?|run club|running club|group (?:run|ride|hike|walk)|social run|ride|club|league|pickup|pick-up|open mic|jam session|jam|karaoke|trivia|game night|board games?|speed dating|singles|volunteer\w*|clean-?up|community|circle|potluck|book club|swing|salsa|square dance|line dancing|dance night|workshop|class|drop-in)\b/i
const meet = (it: CollectionItem) => {
  if (it.judged_by === 'ticketmaster') return false // ticketed seats in a crowd: shows
  if (['music', 'theater', 'film'].includes(it.category) && !/\b(?:open mic|jam|karaoke|sing-?along|drum circle)\b/i.test(it.title)) return false
  if (it.category === 'comedy' && !/\bopen mic\b/i.test(it.title)) return false
  if (has(it, 'sports') && SPECTATOR.test(text(it))) return false
  const soc = dim(it, 'sociality')
  if (soc !== null && soc < 0) return false // something you do on your own
  if (it.category === 'class' || it.category === 'community') return true
  if (careerRow(it)) return true // meetups and mixers are meeting people
  if (it.category === 'market') return has(it, 'social', 'group')
  if (MEET_WORDS.test(it.title)) return true
  return has(it, 'group', 'games', 'dance') && has(it, 'social') && (soc ?? 0) >= 0.5
}

/** The seven, in Alvin's order. `sym` = the Field signals v2 mark file (app/public/symbols/field-signals/<sym>.svg). */
export const COLLECTIONS: Collection[] = [
  { key: 'move', sym: 'outdoors', test: moving },
  { key: 'chill', sym: 'books', test: chill },
  { key: 'make', sym: 'classes', test: make },
  { key: 'shop', sym: 'markets', test: shop },
  { key: 'culture', sym: 'art', test: culture },
  { key: 'show', sym: 'theater', test: show },
  { key: 'people', sym: 'community', test: meet },
]
export const COLLECTION_KEYS = COLLECTIONS.map((c) => c.key)
const BY_KEY = new Map(COLLECTIONS.map((c) => [c.key, c]))
export const isCollection = (key: unknown): key is string => typeof key === 'string' && BY_KEY.has(key)

/** Does this listing belong to the collection? Unknown keys match nothing. */
export function inCollection(it: CollectionItem, key: string): boolean {
  const c = BY_KEY.get(key)
  return c ? c.test(it) : false
}

/** How many of these listings fall in each collection, in collection order. */
export function collectionCounts(items: CollectionItem[]): { key: string; count: number }[] {
  return COLLECTIONS.map((c) => ({ key: c.key, count: items.reduce((n, it) => n + (c.test(it) ? 1 : 0), 0) }))
}
